const cubicBezier = (
	p1x : number,
	p1y : number,
	p2x : number,
	p2y : number
) => {
	const p0x = 0;
	const p0y = 0;
	const p3x = 1;
	const p3y = 1;
	return ((progress : number) => {
		const x = (
			Math.pow(p0x * (1 - progress), 3) +
			Math.pow(3 * p1x * progress * (1 - progress), 2) +
			(3 * p2x * Math.pow(progress, 2) * (1 - progress)) +
			(p3x * Math.pow(progress, 3))
		);
		const y = (
			Math.pow(p0y * (1 - progress), 3) +
			Math.pow(3 * p1y * progress * (1 - progress), 2) +
			(3 * p2y * Math.pow(progress, 2) * (1 - progress)) +
			(p3y * Math.pow(progress, 3))
		);
		return ({ x, y });
	});
};
export class Animation {
	private static runningAnimations = <Set<Animation>>new Set;
	private static renderer(timestamp : DOMHighResTimeStamp) {
		for (const anim of this.runningAnimations) {
			const ellapsedTime = timestamp - anim.lastTimestamp;
			anim.lastTimestamp = timestamp;
			anim.ellapsed += ellapsedTime;
			const inprogress = Math.min((1 / anim.duration) * anim.ellapsed, 1);
			const outprogress = this.timings[anim.timingFunction](inprogress);
			anim.onFrame.call(anim, outprogress);
			if (anim.ellapsed >= anim.duration) {
				if (anim.onEnd) {
					anim.onEnd.call(anim);
				}
				anim.pause();
			}
		}
		requestAnimationFrame(this.renderer);
	};
	static cubicBezier = cubicBezier;
	static timings     : Record<
		'linear' | 'ease' | 'ease-in' | 'ease-out' | 'ease-in-out',
		(progress : number) => {
			x : number;
			y : number;
		}
	> = {
		'linear'      : (progress : number) => ({
			x : progress,
			y : progress
		}),
		'ease'        : cubicBezier(.25, .1, .25, 1),
		'ease-in'     : cubicBezier(.42,  0, 1,   1),
		'ease-out'    : cubicBezier(0,    0, .58, 1),
		'ease-in-out' : cubicBezier(.42,  0, .58, 1)
	};
	static initialize() {
		this.renderer = this.renderer.bind(this);
		requestAnimationFrame(this.renderer);
	};
	ellapsed                = 0;
	isPlaying               = false;
	lastTimestamp           : DOMHighResTimeStamp = 0;
	readonly duration       : number;
	readonly timingFunction : keyof typeof Animation.timings;
	readonly onEnd          : ((this : Animation) => void) | null;
	readonly onPlay         : ((this : Animation) => void) | null;
	readonly onPause        : ((this : Animation) => void) | null;
	readonly onFrame        : (
		this     : Animation,
		progress : {
			x : number;
			y : number;
		}
	) => void;
	readonly data           : {
		[ key : string ] : unknown;
	};
	play() {
		Animation.runningAnimations.add(this);
		this.lastTimestamp = performance.now();
		this.isPlaying     = true;
		if (this.onPlay) {
			this.onPlay.call(this);
		}
	};
	pause() {
		Animation.runningAnimations.delete(this);
		this.isPlaying = false;
		if (this.onPause) {
			this.onPause.call(this);
		}
	};
	replay() {
		this.ellapsed = 0;
		this.lastTimestamp = performance.now();
	};
	setFrameRaw(inprogress : number) {
		const outprogress = Animation.timings[this.timingFunction](inprogress);
		this.ellapsed      = this.duration * outprogress.x;
		this.lastTimestamp = performance.now() - this.ellapsed;
		this.onFrame.call(this, outprogress);
	};
	setFrame(outprogress : {
		x : number;
		y : number;
	}) {
		this.ellapsed = this.duration * outprogress.x;
		this.lastTimestamp = performance.now() - this.ellapsed;
		this.onFrame(outprogress);
	};
	constructor(desc : Animation.IDescriptor) {
		this.duration       = desc.duration;
		this.timingFunction = desc.timingFunction || 'linear';
		this.onFrame        = desc.onFrame;
		this.onEnd          = desc.onEnd || null;
		this.onPlay         = desc.onPlay || null;
		this.onPause        = desc.onPause || null;
		this.data           = desc.data || {};
	};
};
export namespace Animation {
	export interface IDescriptor {
		duration        : number;
		timingFunction? : keyof typeof Animation.timings;
		onEnd?          : (this : Animation) => void;
		onPlay?         : (this : Animation) => void;
		onPause?        : (this : Animation) => void;
		onFrame         : (
			this     : Animation,
			progress : {
				x : number;
				y : number;
			}
		) => void;
		data?           : {
			[ key : string ] : unknown;
		};
	};
};
